import { useState } from 'react'
import type { FormEvent } from 'react'
import { Reveal } from '@/components/Reveal'
import { site } from '@/data/site-content'

const interests = ['Licensing', 'Partnership', 'Seed Purchase', 'General Enquiry']

/**
 * Contact section — the final section of the home page (#contact).
 * All "Work With Us" CTAs across the site scroll here.
 */
export default function ContactSection() {
  const [sent, setSent] = useState(false)

  // No backend yet: the form composes a pre-filled email in the visitor's
  // own mail client instead of pretending to transmit. See PRODUCT.md.
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const interest = String(data.get('interest') ?? 'General Enquiry')
    const lines = [
      `Name: ${data.get('name') ?? ''}`,
      `Company: ${data.get('company') ?? ''}`,
      `Email: ${data.get('email') ?? ''}`,
      `Phone: ${data.get('phone') ?? '—'}`,
      `Interest: ${interest}`,
      '',
      String(data.get('message') ?? ''),
    ]
    const subject = encodeURIComponent(`Website enquiry — ${interest}`)
    const body = encodeURIComponent(lines.join('\n'))
    window.location.href = `mailto:${site.email}?subject=${subject}&body=${body}`
    setSent(true)
  }

  return (
    <section id="contact" className="scroll-mt-[72px] py-24 md:py-32" style={{ backgroundColor: '#E9EDE7' }}>
      <div className="container-hx">
        <Reveal className="eyebrow">Contact</Reveal>
        <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.4vw,2.7rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
          Get in touch
        </Reveal>
        <Reveal as="p" delay={0.16} className="mt-6 max-w-xl leading-[1.85]" style={{ color: '#565650' }}>
          Speak directly about licensing, partnership, or seed enquiries.
        </Reveal>

        <div className="mt-14 grid gap-16 lg:grid-cols-[1.2fr_1fr] lg:gap-24">
          <Reveal>
            {sent ? (
              <div className="border p-12 text-center" style={{ borderColor: '#D9DED5', backgroundColor: '#FAFBF9' }}>
                <div className="font-mono-data text-[11px] uppercase tracking-[0.22em]" style={{ color: '#C98E33' }}>
                  Email composed
                </div>
                <p className="mt-5 text-lg font-light" style={{ color: '#24391C' }}>
                  Your email client should open with your message addressed to{' '}
                  <a href={`mailto:${site.email}`} className="underline underline-offset-4">
                    {site.email}
                  </a>
                  . If it didn't, please email us directly.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="link-underline mt-8"
                  style={{ color: '#565650' }}
                >
                  Write another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-9">
                <div className="grid gap-9 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="eyebrow">Name *</label>
                    <input id="name" name="name" required className="input-hx" placeholder="Your name" autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="company" className="eyebrow">Company *</label>
                    <input id="company" name="company" required className="input-hx" placeholder="Organisation" autoComplete="organization" />
                  </div>
                </div>
                <div className="grid gap-9 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="eyebrow">Email *</label>
                    <input id="email" name="email" required type="email" className="input-hx" placeholder="you@company.com" autoComplete="email" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="eyebrow">Phone</label>
                    <input id="phone" name="phone" type="tel" className="input-hx" placeholder="+27 …" autoComplete="tel" />
                  </div>
                </div>
                <div>
                  <label htmlFor="interest" className="eyebrow">Interest</label>
                  <select id="interest" name="interest" className="input-hx" defaultValue="Licensing">
                    {interests.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="eyebrow">Message *</label>
                  <textarea id="message" name="message" required rows={5} className="input-hx resize-y" placeholder="Tell us about your interest…" />
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <button type="submit" className="btn-primary">
                    Send via Email
                  </button>
                  <span className="text-[12px] font-light" style={{ color: '#96968A' }}>
                    Opens your email client — nothing is stored on this site.
                  </span>
                </div>
              </form>
            )}
          </Reveal>

          <div className="space-y-12">
            <Reveal delay={0.1}>
              <div className="eyebrow">Direct</div>
              <div className="mt-6 space-y-5 text-[15px]">
                <div>
                  <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]" style={{ color: '#96968A' }}>
                    Email
                  </div>
                  <a href={`mailto:${site.email}`} className="mt-1 block" style={{ color: '#24391C' }}>
                    {site.email}
                  </a>
                </div>
                <div>
                  <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]" style={{ color: '#96968A' }}>
                    Phone
                  </div>
                  <span className="mt-1 block" style={{ color: '#96968A' }}>
                    [TO BE FILLED]
                  </span>
                </div>
                <div>
                  <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]" style={{ color: '#96968A' }}>
                    Location
                  </div>
                  <span className="mt-1 block" style={{ color: '#96968A' }}>
                    South Africa · [region to be filled]
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18} className="border p-8" style={{ borderColor: '#D9DED5', backgroundColor: '#FAFBF9' }}>
              <div className="eyebrow">Spec Sheets & Trial Data</div>
              <div className="mt-4 text-[15px] font-medium" style={{ color: '#24391C' }}>
                Evaluating Hylix genetics?
              </div>
              <p className="mt-3 text-[14px] leading-[1.8]" style={{ color: '#565650' }}>
                Complete spec sheets and multi-season trial data are available to qualified seed
                companies on request. Mention the hybrid or line you're interested in and we will
                respond promptly.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}
